import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {Globe, Loader2, Save, ChevronRight, Crown, CheckCircle, XCircle, AlertTriangle, RefreshCw, Moon, Sun, Monitor, Shield } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { getTier1Languages, setStoredLanguage, getLanguageByCode } from '../lib/languages';
import { useTranslation } from '../i18n';
import SecurityTab from '../components/SecurityTab';
import ReferralTab from '../components/ReferralTab';
import NotificationsTab from '../components/NotificationsTab';
import QRShareSection from '../components/QRShareSection';
import LanguagePickerDialog from '../components/LanguagePickerDialog';
import { LogoUploader } from '../components/settings/LogoUploader';
import { getLogoUrl } from '../lib/api';

import { Navigate } from 'react-router-dom';
import GoogleCalendarIntegration from "../components/GoogleCalendarIntegration";
import { canWrite, canManageTeam, canManageBilling, isViewer } from '../lib/roleUtils';
import PaymentSettingsTab from '../components/PaymentSettingsTab';
import { PLAN_DETAILS, getPlanDisplayName } from '../lib/planGating';

function SettingsPageInner() {
  const { user, updateUser, setPreferredLanguage, updateActiveOrganization } = useAuth();
  const queryClient = useQueryClient();

  const { t, setLanguage } = useTranslation();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'profile';

  // Language picker dialog state
  const [billingAnnual, setBillingAnnual] = useState(false);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [logoMode, setLogoMode] = useState('upload');
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      // Prefer server-stored preference, fall back to localStorage
      const serverPref = user?.theme_preference;
      const localPref = localStorage.getItem('pf-theme');
      const pref = serverPref || localPref || 'light';
      // Sync localStorage if server had a preference
      if (serverPref && serverPref !== localPref) {
        localStorage.setItem('pf-theme', serverPref);
        const resolved = serverPref === 'system'
          ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
          : serverPref;
        document.documentElement.classList.toggle('dark', resolved === 'dark');
      }
      return pref;
    }
    return 'light';
  });

  const applyTheme = (newTheme) => {
    const resolved = newTheme === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : newTheme;
    document.documentElement.classList.toggle('dark', resolved === 'dark');
    localStorage.setItem('pf-theme', newTheme);
    setTheme(newTheme);
    // Persist to user profile for cross-device sync
    api.put('/auth/me', { theme_preference: newTheme }).catch(() => {});
  }; // 'url' | 'upload'
  // Calendar feed
  const [calendarFeedUrl, setCalendarFeedUrl] = useState(null);
  const [generatingFeed, setGeneratingFeed] = useState(false);

  const generateCalendarFeed = async () => {
    setGeneratingFeed(true);
    try {
      const res = await api.post('/meetings/calendar/generate-token');
      setCalendarFeedUrl(res.feed_url);
      toast.success(t('settings.calendarFeedGenerated') || 'Calendar feed URL generated!');
    } catch (err) {
      toast.error('Failed to generate calendar feed');
    }
    setGeneratingFeed(false);
  };


  // Get Tier 1 languages for display
  const languages = getTier1Languages();

  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    preferred_language: user?.preferred_language || 'en',
    role: user?.role || 'individual'
  });

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.get('/organizations')
  });

  const organization = organizations?.[0];

  const { data: orgPlanInfo, isLoading: planLoading } = useQuery({
    queryKey: ['org-plan', organization?._id],
    queryFn: () => api.get(`/organizations/${organization._id}/plan`),
    enabled: !!organization?._id
  });

  const [orgData, setOrgData] = useState({
    name: organization?.name || '',
    type: organization?.type || 'manufacturing',
    billing_email: organization?.billing_email || '',
    logo_url: organization?.logo_url || '',
    primary_color: organization?.primary_color || '#000000',
    accent_color: organization?.accent_color || '#000000',
    custom_domain: organization?.custom_domain || ''
  });

  React.useEffect(() => {
    if (organization) {
      setOrgData({
        name: organization.name || '',
        type: organization.type || 'manufacturing',
        billing_email: organization.billing_email || '',
        logo_url: organization.logo_url || '',
        primary_color: organization.primary_color || '#000000',
        accent_color: organization.accent_color || '#000000',
        custom_domain: organization.custom_domain || ''
      });
    }
  }, [organization]);

  // Handle language change from dialog - saves and refreshes
  const handleLanguageChange = async (newLanguage) => {
    try {
      // Update on server
      const response = await api.put('/auth/me', {
        ...profileData,
        preferred_language: newLanguage
      });

      // Update localStorage
      setStoredLanguage(newLanguage);

      // Update local state
      setProfileData(prev => ({ ...prev, preferred_language: newLanguage }));

      // Update auth context
      updateUser(response);

      // Show success message
      toast.success(t('settings.profile.saved'));

      // Hard refresh to apply language changes throughout the app
      setTimeout(() => {
        window.location.reload();
      }, 500);

      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateProfileMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.put('/auth/me', data);
      return response;
    },
    onSuccess: (data) => {
      updateUser(data);
      toast.success(t('settings.profile.saved'));
    },
    onError: (error) => {
      toast.error(error.message || t('error.generic'));
    }
  });

  const updateOrgMutation = useMutation({
    mutationFn: (data) => api.put(`/organizations/${organization?._id}`, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['organizations']);
      // Update the active organization in auth context directly with the
      // server response — this triggers the useEffect in AppLayout to
      // apply new CSS variables instantly, no page reload needed.
      updateActiveOrganization(data);
      toast.success(t('settings.org.saved'));
    },
    onError: (error) => {
      toast.error(error.message || t('error.generic'));
    }
  });

  // ── Subscription data ─────────────────────────────────────
  const { data: subStatus, isLoading: subLoading, refetch: refetchSub } = useQuery({
    queryKey: ['subscription-status'],
    queryFn: () => api.get('/subscriptions/status'),
    enabled: !!organization?._id,
    staleTime: 30000,
  });

  const { data: planUsage } = useQuery({
    queryKey: ["plan-usage"],
    queryFn: () => api.get("/subscriptions/usage"),
    enabled: !!organization?._id,
    staleTime: 30000,
  });

  // Show success/cancelled toast when Stripe redirects back
  useEffect(() => {
    const subscribed = searchParams.get('subscribed');
    const checkout = searchParams.get('checkout');
    if (subscribed === 'true') {
      // Refresh subscription data so the UI reflects the new plan immediately
      refetchSub();
      queryClient.invalidateQueries(['org-plan', organization?._id]);
    }
    if (checkout === 'cancelled') {
      toast.info('Checkout cancelled. You can upgrade any time from this page.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribeCheckoutMutation = useMutation({
    mutationFn: (params) => api.post('/subscriptions/checkout', params || {}),
    onSuccess: (data) => {
      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    },
    onError: (error) => {
      const msg = error.response?.data?.detail || error.message || 'Failed to start checkout';
      toast.error(msg);
    },
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => api.post('/subscriptions/cancel'),
    onSuccess: (data) => {
      toast.success('Subscription cancelled. Pro access continues until end of billing period.');
      refetchSub();
      queryClient.invalidateQueries(['org-plan', organization?._id]);
    },
    onError: (error) => {
      const msg = error.response?.data?.detail || error.message || 'Failed to cancel subscription';
      toast.error(msg);
    },
  });

  const reactivateSubscriptionMutation = useMutation({
    mutationFn: () => api.post('/subscriptions/reactivate'),
    onSuccess: () => {
      toast.success('Subscription reactivated! It will continue to renew automatically.');
      refetchSub();
      queryClient.invalidateQueries(['org-plan', organization?._id]);
    },
    onError: (error) => {
      const msg = error.response?.data?.detail || error.message || 'Failed to reactivate subscription';
      toast.error(msg);
    },
  });

  const portalMutation = useMutation({
    mutationFn: () => api.post("/subscriptions/portal"),
    onSuccess: (data) => { window.location.href = data.portal_url; },
    onError: (error) => { toast.error(error.response?.data?.detail || "Failed to open billing portal"); },
  });

  // Get current language display info
  const currentLanguageInfo = getLanguageByCode(profileData.preferred_language);

  // If the user is a team member, redirect them away from the entire settings page
  if (user?.is_user) {
    return <Navigate to="/analytics" replace />;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold">{t('settings.title')}</h1>
        <p className="text-muted-foreground">{t('settings.subtitle')}</p>
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList>
          <TabsTrigger value="profile" className="flex items-center gap-2">
            {t('settings.tabs.profile')}
          </TabsTrigger>
          {canManageTeam(user) && <TabsTrigger value="organization" className="flex items-center gap-2">
            {t('settings.tabs.organization')}
          </TabsTrigger>}
          {canManageTeam(user) && <TabsTrigger value="branding" className="flex items-center gap-2">
            {t("settings.tabs.branding") || "Branding"}
          </TabsTrigger>}
          <TabsTrigger value="sharing" className="flex items-center gap-2">
            {t('settings.tabs.sharing') || 'Sharing'}
          </TabsTrigger>
          {canManageBilling(user) && <TabsTrigger value="payments" className="flex items-center gap-2">
            {t("settings.tabs.payments") || "Payments"}
          </TabsTrigger>}
          {canManageBilling(user) && <TabsTrigger value="plan" className="flex items-center gap-2">
            {t("settings.tabs.plan") || "Plan"}
          </TabsTrigger>}
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            {t("settings.security") || "Security"}
          </TabsTrigger>
          <TabsTrigger value="referrals" className="flex items-center gap-2">
            {t("settings.referrals") || "Referrals"}
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            {t("settings.notifications") || "Notifications"}
          </TabsTrigger>
          <TabsTrigger value="integrations" className="flex items-center gap-2">
            {t("settings.tabs.integrations") || "Integrations"}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.profile.title')}</CardTitle>
              <CardDescription>{t('settings.profile.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => { e.preventDefault(); updateProfileMutation.mutate({ name: profileData.name, role: profileData.role }); }} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('settings.profile.name')}</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('settings.profile.email')}</Label>
                  <Input
                    id="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">{t('settings.profile.emailNote')}</p>
                </div>

                {/* Account Type */}
                <div className="space-y-2">
                  <Label>{t('settings.profile.accountType') || 'Account Type'}</Label>
                  <Select
                    value={profileData.role}
                    onValueChange={(value) => setProfileData(prev => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger data-testid="account-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">{t("settings.individual")}</SelectItem>
                      <SelectItem value="business">{t("settings.business")}</SelectItem>
                      <SelectItem value="buyer">{t("settings.buyer")}</SelectItem>
                      <SelectItem value="seller">{t("settings.seller")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {t('settings.profile.accountTypeHelp') || 'Choose the type that best describes you'}
                  </p>
                </div>

                {/* Language Selection - Opens Dialog */}
                <div className="space-y-2">
                  <Label>
                    <div className="flex items-center gap-2">
                      {t('settings.profile.language')}
                    </div>
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    {t('settings.profile.languageHelp')}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between h-auto py-3"
                    onClick={() => setLanguageDialogOpen(true)}
                    data-testid="language-selector-button"
                  >
                    <div className="flex items-center gap-3">
                      <Globe className="h-5 w-5 text-primary" />
                      <div className="text-left">
                        <p className="font-semibold" dir={currentLanguageInfo?.rtl ? 'rtl' : 'ltr'}>
                          {currentLanguageInfo?.nativeName || 'English'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {currentLanguageInfo?.name || 'English'}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Click to change language and hear audio samples
                  </p>
                </div>

                <Button type="submit" disabled={updateProfileMutation.isPending}>
                  {updateProfileMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('settings.profile.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {t('settings.profile.save')}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
          {/* Theme Preference */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                {t("settings.appearance") || "Appearance"}
              </CardTitle>
              <CardDescription>{t("settings.chooseYourPreferredTheme")}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => applyTheme('light')}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    theme === 'light' ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'
                  }`}
                >
                  <Sun className="h-6 w-6" />
                  <span className="text-sm font-medium">{t("settings.light")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => applyTheme('dark')}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    theme === 'dark' ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'
                  }`}
                >
                  <Moon className="h-6 w-6" />
                  <span className="text-sm font-medium">{t("settings.dark")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => applyTheme('system')}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                    theme === 'system' ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'
                  }`}
                >
                  <Monitor className="h-6 w-6" />
                  <span className="text-sm font-medium">{t("settings.system")}</span>
                </button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="organization" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.org.title')}</CardTitle>
              <CardDescription>{t('settings.org.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              {organization ? (
                <form onSubmit={(e) => { e.preventDefault(); updateOrgMutation.mutate({ name: orgData.name, type: orgData.type, billing_email: orgData.billing_email }); }} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="org-name">{t('settings.org.name')}</Label>
                    <Input
                      id="org-name"
                      value={orgData.name}
                      onChange={(e) => setOrgData(prev => ({ ...prev, name: e.target.value }))}
                      data-testid="org-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('settings.org.type')}</Label>
                    <Select
                      value={orgData.type}
                      onValueChange={(value) => setOrgData(prev => ({ ...prev, type: value }))}
                    >
                      <SelectTrigger data-testid="org-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="manufacturing">{t("settings.manufacturing")}</SelectItem>
                        <SelectItem value="insurance">{t("settings.insurance")}</SelectItem>
                        <SelectItem value="real_estate">{t("settings.realEstate")}</SelectItem>
                        <SelectItem value="automotive">{t("settings.automotive")}</SelectItem>
                        <SelectItem value="import_export">{t("settings.importexport")}</SelectItem>
                        <SelectItem value="other">{t("settings.other")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="billing-email">{t('settings.org.billingEmail')}</Label>
                    <Input
                      id="billing-email"
                      type="email"
                      value={orgData.billing_email}
                      onChange={(e) => setOrgData(prev => ({ ...prev, billing_email: e.target.value }))}
                    />
                  </div>
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('settings.org.plan')}</span>
                      <span className="font-medium">{getPlanDisplayName(organization.plan)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-muted-foreground">{t('settings.org.verification')}</span>
                      <span className="font-medium capitalize">{organization.verified_status || 'Unverified'}</span>
                    </div>
                  </div>
                  <Button type="submit" disabled={updateOrgMutation.isPending}>
                    {updateOrgMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('settings.org.saving')}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {t('settings.org.save')}
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <p className="text-sm text-muted-foreground">{t('settings.org.noOrg')}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="branding" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.brandingWhitelabel")}</CardTitle>
              <CardDescription>{t("settings.customizeThePocketfilerExperience")}</CardDescription>
            </CardHeader>
            <CardContent>
              {organization ? (
                <form onSubmit={(e) => { e.preventDefault(); updateOrgMutation.mutate({ logo_url: orgData.logo_url, primary_color: orgData.primary_color, accent_color: orgData.accent_color }); }} className="space-y-6">
                  <div className="space-y-4 border p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Label>{t("settings.organizationLogo")}</Label>
                      <div className="flex items-center gap-2 bg-muted p-1 rounded-md">
                        <Button
                          type="button"
                          variant={logoMode === 'upload' ? 'secondary' : 'ghost'}
                          size="sm"
                          className="h-7 text-xs px-3"
                          onClick={() => setLogoMode('upload')}
                        >
                          Upload File
                        </Button>
                        <Button
                          type="button"
                          variant={logoMode === 'url' ? 'secondary' : 'ghost'}
                          size="sm"
                          className="h-7 text-xs px-3"
                          onClick={() => setLogoMode('url')}
                        >
                          Link URL
                        </Button>
                      </div>
                    </div>

                    {logoMode === 'url' ? (
                      <div className="space-y-2 mt-2">
                        <Input
                          id="logo-url"
                          placeholder={t("settings.ph.logoUrl") || "https://example.com/logo.png"}
                          value={orgData.logo_url}
                          onChange={(e) => setOrgData(prev => ({ ...prev, logo_url: e.target.value }))}
                        />
                        <p className="text-xs text-muted-foreground">{t("settings.provideADirectLink")}</p>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <LogoUploader
                          currentLogoUrl={getLogoUrl(orgData.logo_url, organization?._id)}
                          onLogoUploaded={(url) => {
                            setOrgData(prev => ({ ...prev, logo_url: url }));
                            // Auto-save logo to backend immediately
                            updateOrgMutation.mutate({ ...orgData, logo_url: url });
                          }}
                        />
                        <p className="text-xs text-muted-foreground mt-2 text-center">
                          The logo is automatically saved to your branding configuration.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-4 border p-4 rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="primary-color">{t("settings.primaryThemeColor")}</Label>
                      <div className="flex items-center gap-4">
                        <Input
                          id="primary-color"
                          type="color"
                          className="w-16 h-12 p-1 cursor-pointer"
                          value={orgData.primary_color}
                          onChange={(e) => setOrgData(prev => ({ ...prev, primary_color: e.target.value }))}
                        />
                        <Input
                          type="text"
                          value={orgData.primary_color}
                          onChange={(e) => setOrgData(prev => ({ ...prev, primary_color: e.target.value }))}
                          className="w-32 uppercase font-mono"
                          maxLength={7}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{t("settings.selectAHexColor")}</p>
                    </div>
                  </div>

                  <div className="space-y-4 border p-4 rounded-lg">
                    <div className="space-y-2">
                      <Label htmlFor="accent-color">{t("settings.accentHoverColor")}</Label>
                      <div className="flex items-center gap-4">
                        <Input
                          id="accent-color"
                          type="color"
                          className="w-16 h-12 p-1 cursor-pointer"
                          value={orgData.accent_color}
                          onChange={(e) => setOrgData(prev => ({ ...prev, accent_color: e.target.value }))}
                        />
                        <Input
                          type="text"
                          value={orgData.accent_color}
                          onChange={(e) => setOrgData(prev => ({ ...prev, accent_color: e.target.value }))}
                          className="w-32 uppercase font-mono"
                          maxLength={7}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{t("settings.selectAHexColor1")}</p>
                    </div>
                  </div>

                  {/* 
                  <div className="space-y-2">
                    <Label htmlFor="custom-domain">{t("settings.customDomain")}</Label>
                    <Input
                      id="custom-domain"
                      placeholder={t("settings.ph.customDomain") || "deals.yourcompany.com"}
                      value={orgData.custom_domain}
                      onChange={(e) => setOrgData(prev => ({ ...prev, custom_domain: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">{t("settings.ensureYouConfigureA")}</p>
                  </div>
                  */}

                  <Button type="submit" disabled={updateOrgMutation.isPending}>
                    {updateOrgMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {t("settings.saveBranding") || "Save Branding"}
                      </>
                    )}
                  </Button>
                </form>
              ) : (
                <p className="text-sm text-muted-foreground">{t('settings.org.noOrg')}</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sharing" className="mt-6">
          <QRShareSection />
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <PaymentSettingsTab />
        </TabsContent>

        <TabsContent value="plan" className="mt-6">
          <div className="space-y-6">
            {/* Current Plan + Usage */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-yellow-500" />
                  {t("settings.yourPlan") || "Your Plan"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {subLoading ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        orgPlanInfo?.plan === 'free' ? 'bg-muted text-muted-foreground border' :
                        orgPlanInfo?.plan === 'pro' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                        'bg-blue-100 text-blue-800 border border-blue-300'
                      }`}>
                        {({'free':'Individual Free','pro':'Individual Pro','buyer_free':'Buyer Free','buyer_pro':'Buyer Pro','buyer_enterprise':'Buyer Enterprise','supplier_listed':'Listed','supplier_verified':'Verified','supplier_featured':'Featured','starter':t('settings.plan.starter')||'Starter','growth':t('settings.plan.growth')||'Growth','scale':t('settings.plan.scale')||'Scale','enterprise':t('settings.plan.enterprise')||'Enterprise'}[orgPlanInfo?.plan] || (orgPlanInfo?.plan || 'free').charAt(0).toUpperCase() + (orgPlanInfo?.plan || 'free').slice(1))} Plan
                      </div>
                      {subStatus?.subscription_status && subStatus.subscription_status !== 'canceled' && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
                          subStatus.subscription_status === 'active' ? 'bg-green-50 text-green-700 border-green-200' :
                          subStatus.subscription_status === 'past_due' ? 'bg-red-50 text-red-700 border-red-200' :
                          'bg-muted text-muted-foreground border'
                        }`}>
                          {subStatus.subscription_status === 'active' ? 'Active' :
                           subStatus.subscription_status === 'past_due' ? 'Payment past due' :
                           subStatus.subscription_status}
                        </span>
                      )}
                    </div>

                    {/* Usage Meters */}
                    {planUsage && (
                      <div className="grid gap-3 sm:grid-cols-3">
                        {[
                          { label: 'AI Contracts', used: planUsage.contracts?.used || 0, limit: planUsage.contracts?.limit, suffix: '/mo' },
                          { label: 'Storage', used: planUsage.storage?.used_mb || 0, limit: planUsage.storage?.limit_mb, unit: 'MB' },
                          { label: 'Team Members', used: planUsage.team?.used || 0, limit: planUsage.team?.limit },
                        ].map(({ label, used, limit, suffix, unit }) => (
                          <div key={label} className="rounded-lg border p-3">
                            <p className="text-xs text-muted-foreground mb-1">{label}</p>
                            <p className="text-sm font-semibold">
                              {unit ? `${used} ${unit}` : used} {limit > 0 ? `/ ${limit}${unit ? ` ${unit}` : ''}${suffix || ''}` : '(unlimited)'}
                            </p>
                            {limit > 0 && (
                              <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${used >= limit ? 'bg-red-500' : used >= limit * 0.8 ? 'bg-amber-500' : 'bg-blue-500'}`}
                                  style={{ width: `${Math.min(100, (used / limit) * 100)}%` }}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Cancellation notice */}
                    {subStatus?.cancel_at_period_end && subStatus?.current_period_end && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium text-amber-800">{t("settings.cancellationScheduled")}</p>
                          <p className="text-amber-700">
                            Access ends on{' '}
                            <strong>{new Date(subStatus.current_period_end).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Past-due warning */}
                    {subStatus?.subscription_status === 'past_due' && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm">
                        <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                        <p className="text-red-800">{t("settings.yourLastPaymentFailed")}</p>
                      </div>
                    )}

                    {/* Renewal date */}
                    {subStatus?.subscription_status === 'active' && !subStatus?.cancel_at_period_end && subStatus?.current_period_end && (
                      <p className="text-sm text-muted-foreground">
                        Next billing: <strong>{new Date(subStatus.current_period_end).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                      </p>
                    )}

                    {/* Manage / Cancel / Reactivate */}
                    {!user?.is_user && subStatus?.has_subscription && (
                      <div className="pt-2 flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => portalMutation.mutate()} disabled={portalMutation?.isPending}>
                          Manage Billing
                        </Button>
                        {subStatus?.cancel_at_period_end ? (
                          <Button variant="outline" size="sm" onClick={() => reactivateSubscriptionMutation.mutate()} disabled={reactivateSubscriptionMutation.isPending}>
                            {reactivateSubscriptionMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                            Reactivate
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive"
                            onClick={() => { if (window.confirm('Cancel subscription? You keep access until end of billing period.')) cancelSubscriptionMutation.mutate(); }}
                            disabled={cancelSubscriptionMutation.isPending}>
                            Cancel Subscription
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Plan Cards */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle>{t("settings.chooseYourPlan")}</CardTitle>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={!billingAnnual ? 'font-semibold' : 'text-muted-foreground'}>{t("settings.monthly")}</span>
                    <button
                      onClick={() => setBillingAnnual(!billingAnnual)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${billingAnnual ? 'bg-blue-500' : 'bg-gray-300'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${billingAnnual ? 'translate-x-5' : ''}`} />
                    </button>
                    <span className={billingAnnual ? 'font-semibold' : 'text-muted-foreground'}>Annual <span className="text-green-600 text-xs font-medium">{t("settings.save20")}</span></span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {[
                    ...PLAN_DETAILS.map(p => ({ name: p.key, label: p.name, price: p.price, annual: p.annual, users: p.users, contracts: p.contracts, storage: p.storage, popular: p.popular, highlights: p.highlights })),
                  ].map((plan) => {
                    const isCurrent = orgPlanInfo?.plan === plan.name || (!orgPlanInfo?.plan && plan.name === 'free');
                    const currentIdx = ['free','pro','starter','growth','scale','enterprise'].indexOf(orgPlanInfo?.plan || 'free');
                    const planIdx = ['free','pro','buyer_free','buyer_pro','buyer_enterprise','supplier_listed','supplier_verified','supplier_featured','starter','growth','scale','enterprise'].indexOf(plan.name);
                    const isUpgrade = planIdx > currentIdx;
                    const displayPrice = billingAnnual ? plan.annual : plan.price;

                    return (
                      <div key={plan.name} className={`relative rounded-xl border p-4 flex flex-col ${isCurrent ? 'border-blue-500 bg-blue-500/10 ring-1 ring-blue-500' : plan.popular ? 'border-yellow-500 bg-yellow-500/10' : ''}`}>
                        {isCurrent && <div className="absolute -top-2.5 left-4 px-2 py-0.5 text-xs font-semibold bg-blue-500 text-white rounded-full">{t("settings.current")}</div>}
                        {plan.popular && !isCurrent && <div className="absolute -top-2.5 left-4 px-2 py-0.5 text-xs font-semibold bg-yellow-500 text-white rounded-full">{t("settings.popular")}</div>}
                        <h3 className="font-semibold text-lg">{plan.label}</h3>
                        <div className="mt-1 mb-3">
                          {displayPrice !== null ? (
                            <span className="text-2xl font-bold">${displayPrice}<span className="text-sm font-normal text-muted-foreground">/mo</span></span>
                          ) : (
                            <span className="text-lg font-semibold text-muted-foreground">{t("settings.contactSales")}</span>
                          )}
                        </div>
                        <div className="space-y-1 text-sm text-muted-foreground mb-3 flex-1">
                          <p>{plan.users} user{plan.users !== '1' ? 's' : ''} · {plan.contracts} · {plan.storage}</p>
                          {plan.highlights.map((h) => (
                            <p key={h} className="flex items-center gap-1.5">
                              <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" /> {h}
                            </p>
                          ))}
                        </div>
                        {!user?.is_user && isUpgrade && plan.name !== 'enterprise' && (
                          <Button
                            size="sm"
                            className={plan.popular ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : ''}
                            onClick={() => subscribeCheckoutMutation.mutate({ plan: plan.name, billing_cycle: billingAnnual ? 'annual' : 'monthly' })}
                            disabled={subscribeCheckoutMutation.isPending}
                          >
                            {subscribeCheckoutMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                            Upgrade to {plan.label}
                          </Button>
                        )}
                        {plan.name === 'enterprise' && isUpgrade && (
                          <Button size="sm" variant="outline" onClick={() => window.location.href = 'mailto:info@pocketfiler.com?subject=Enterprise%20Inquiry'}>
                            Contact Sales
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Team-member notice */}
            {user?.is_user && (
              <p className="text-sm text-muted-foreground text-center py-2">
                Only the organization owner can change the plan.
              </p>
            )}
          </div>
        </TabsContent>

        {/* Integrations Tab */}

        <TabsContent value="security" className="mt-6">
          <SecurityTab />
        </TabsContent>

        <TabsContent value="referrals" className="mt-6">
          <ReferralTab />
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <NotificationsTab />
        </TabsContent>

        <TabsContent value="integrations" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.googleCalendar")}</CardTitle>
              <CardDescription>{t("settings.syncYourPocketfilerMeetings")}</CardDescription>
            </CardHeader>
            <CardContent>
              <GoogleCalendarIntegration />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Language Picker Dialog */}
      <LanguagePickerDialog
        open={languageDialogOpen}
        onOpenChange={setLanguageDialogOpen}
        currentLanguage={profileData.preferred_language}
        onLanguageChange={handleLanguageChange}
      />
    </div>
  );
}


export default function SettingsPage() {
  try {
    return <SettingsPageInner />;
  } catch (e) {
    return (
      <div style={{padding: 40, color: 'red', fontFamily: 'monospace', whiteSpace: 'pre-wrap'}}>
        <h2>{t("settings.settingsPageErrorDebug")}</h2>
        <p>{e?.toString()}</p>
        <p>{e?.stack}</p>
      </div>
    );
  }
}
